import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { formatDate, timeAgo } from '../../utils/dateUtils';

function initials(username) {
  return username ? username.slice(0, 2).toUpperCase() : '??';
}

function ReportDetailModal({ report, onClose, onToggleResolve }) {
  const { t } = useTranslation('admin');
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!report) return undefined;
    let cancelled = false;
    setStatus('loading');
    setUser(null);
    api.get(`/users/${report.userId}`)
      .then(({ user: fetchedUser }) => {
        if (cancelled) return;
        setUser(fetchedUser);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [report]);

  if (!report) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card modal-content report-detail-modal" onClick={(event) => event.stopPropagation()}>
        <div className="report-detail-top">
          <span className={`report-status-pill ${report.status}`}>{report.status === 'resolved' ? t('reportsTable.resolved') : t('reportsTable.open')}</span>
          <button type="button" className="text-btn icon-btn" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        <h3>{report.subject}</h3>
        <p className="report-detail-message">{report.message}</p>
        <p className="report-detail-meta">{t('reportDetailModal.submittedAt', { date: formatDate(report.createdAt), relative: timeAgo(report.createdAt) })}</p>

        <div className="report-detail-user">
          <h4>{t('reportDetailModal.reportedBy')}</h4>
          {status === 'loading' && <p className="chart-empty">{t('reportDetailModal.loadingUser')}</p>}
          {status === 'error' && <p className="chart-empty">{t('reportDetailModal.couldNotLoadUser')}</p>}
          {status === 'ready' && user && (
            <>
              <div className="account-detail-header card">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="profile-avatar profile-avatar-img profile-avatar-large" />
                ) : (
                  <span className="profile-avatar profile-avatar-large">{initials(user.username)}</span>
                )}
                <div className="account-detail-heading">
                  <div className="account-detail-title-row">
                    <h2>{user.username}</h2>
                  </div>
                  <span className={`role-pill ${user.role}`}>{t(`roleLabels.${user.role}`)}</span>
                </div>
              </div>
              <div className="card account-detail-grid">
                <div className="account-detail-field">
                  <span className="account-detail-label">{t('reportDetailModal.email')}</span>
                  <span className="account-detail-value">{user.email || t('dash')}</span>
                </div>
                <div className="account-detail-field">
                  <span className="account-detail-label">{t('reportDetailModal.joined')}</span>
                  <span className="account-detail-value">{user.createdAt ? formatDate(user.createdAt) : t('dash')}</span>
                </div>
                <div className="account-detail-field">
                  <span className="account-detail-label">{t('reportDetailModal.subscription')}</span>
                  <span className="account-detail-value"><span className={`package-pill ${user.subscriptionPackage || 'free'}`}>{t(`packageLabels.${user.subscriptionPackage || 'free'}`)}</span></span>
                </div>
                <div className="account-detail-field">
                  <span className="account-detail-label">{t('reportDetailModal.lastLogin')}</span>
                  <span className="account-detail-value">{user.lastLoginAt ? timeAgo(user.lastLoginAt) : t('never')}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="text-btn" onClick={onClose}>{t('reportDetailModal.close')}</button>
          {onToggleResolve && (
            <button
              type="button"
              className="primary-btn"
              onClick={() => onToggleResolve(report, report.status === 'resolved' ? 'open' : 'resolved')}
            >
              {report.status === 'resolved' ? t('reportDetailModal.reopen') : t('reportDetailModal.markResolved')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportDetailModal;
